import { useMemo } from 'react';

export interface ArrayDiff<T> {
  toCreate: Array<T>;
  toUpdate: Array<T>;
  toDelete: Array<T>;
}

interface UseArrayDiffOptions<T> {
  current: Array<T>;
  initial: Array<T>;
  getId: (item: T) => string | undefined;
}

/**
 * Hook to calculate differences between initial and current array states
 * Returns arrays of items to create, update, and delete
 *
 * Uses JSON.stringify for comparison to detect any field changes automatically
 *
 * @param options - Configuration object
 * @param options.current - Current array state
 * @param options.initial - Initial array state (snapshot)
 * @param options.getId - Function to get unique identifier from item
 * @returns Object with toCreate, toUpdate, and toDelete arrays
 *
 * @example
 * const diff = useArrayDiff({
 *   current: options,
 *   initial: initialOptions,
 *   getId: (opt) => opt.id,
 * });
 */
export function useArrayDiff<T>({ current, initial, getId }: UseArrayDiffOptions<T>): ArrayDiff<T> {
  return useMemo(() => {
    const initialMap = new Map(initial.map((item) => [getId(item) || '', item]));
    const currentMap = new Map(current.map((item) => [getId(item) || '', item]));

    const toCreate: Array<T> = [];
    const toUpdate: Array<T> = [];
    const toDelete: Array<T> = [];

    // Find items to create (no ID) or update (has ID but changed)
    current.forEach((currentItem) => {
      const id = getId(currentItem);
      if (!id) {
        toCreate.push(currentItem);
      } else {
        const initialItem = initialMap.get(id);
        if (initialItem) {
          const hasChanged = JSON.stringify(initialItem) !== JSON.stringify(currentItem);
          if (hasChanged) {
            toUpdate.push(currentItem);
          }
        }
      }
    });

    // Find items to delete (were in initial but not in current)
    initial.forEach((initialItem) => {
      const id = getId(initialItem);
      if (id && !currentMap.has(id)) {
        toDelete.push(initialItem);
      }
    });

    return { toCreate, toUpdate, toDelete };
  }, [current, initial, getId]);
}
